// src/app/layouts/components/aside/AsideMenuList.jsx

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { NavLink } from "react-router-dom";
import { checkIsActive } from "../../../../_helpers";
import { shallowEqual, useSelector } from "react-redux";
import { useOrgConfig } from "../../../../../hooks/useOrgConfig";
import { useSubscriptionData } from "../../../../../hooks/useSubscriptionStatus";
import { OrgSwitcher } from '../../../../../app/components/OrgSwitcher/OrgSwitcher';

import {
  LayoutDashboard,
  ScanLine,
  Activity,
  Brain,
  AlertTriangle,
  Shield,
  Ban,
  Users,
  UserCheck,
  MapPin,
  Zap,
  Building2,
  BarChart2,
  Code2,
  ChevronRight,
  ShieldAlert,
  Briefcase,
  Wrench,
  AlertCircle,
  XCircle,
  CalendarDays,
  Wallet,
  ShoppingBag,
  Settings,
  Fingerprint,
} from 'lucide-react';

import "./aside-menu-list.scss";

// ── Icon size constants ────────────────────────────────────────────────────────
// One size for everything — no visual noise from mixed sizing
const ICON_SIZE   = 15;
const ICON_STROKE = 1.7;

export function AsideMenuList({ layoutProps }) {
  const location = useLocation();
  const navigate  = useNavigate();

  const { isTrial, isPastDue, isSuspended, trialDaysLeft } = useSubscriptionData();
  const { getLabel, showModule, sidebarConfig } = useOrgConfig();

  const [expanded, setExpanded] = useState({
    main: true, security: false, management: false,
    integrations_root: true, system: false,
  });

  const { activeIncidents, activeAlerts } = useSelector(s => ({
    activeIncidents: s.dashboard?.active_incidents_count || 0,
    activeAlerts:    s.dashboard?.active_alerts_count    || 0,
  }), shallowEqual);

  // const isActive = (url) => checkIsActive(location, url);

 const isActive = (url) => {
    // 1. Handle links with query parameters (System tabs)
    if (url.includes('?')) {
      const [path, search] = url.split('?');
      
      // Basic path check first
      if (location.pathname !== path) return false;

      // Extract the tab value from the link (e.g., 'billing')
      const linkParams = new URLSearchParams(search);
      const linkTab = linkParams.get('tab');

      // Extract the tab value from the current browser URL
      const currentParams = new URLSearchParams(location.search);
      const currentTab = currentParams.get('tab');

      return linkTab === currentTab;
    }
    
    // 2. Default behavior for standard links
    return checkIsActive(location, url);
  };

  const toggle = (key) => {
    setExpanded(prev => {
      if (prev[key]) return { ...prev, [key]: false };
      const n = { main: true };
      Object.keys(prev).forEach(k => { if (k !== 'main') n[k] = k === key; });
      return n;
    });
  };

  const sections = [
    {
      key: 'main', title: null, icon: null,
      items: [
        { name: getLabel('dashboard'),     link: '/dashboard',            icon: LayoutDashboard },
        { name: getLabel('accessControl'), link: '/access-control',       icon: showModule('showGateControl') ? ScanLine : ScanLine },
        { name: 'Operations',              link: '/operations',            icon: Activity },
        { name: 'Safety Intelligence',     link: '/network-intelligence',  icon: Brain, badge: 'AI' },
      ],
    },
    {
      key: 'security', title: 'Security', icon: ShieldAlert,
      items: [
        { name: getLabel('incidents'),    link: '/incidents',     icon: AlertTriangle, badge: activeIncidents, show: showModule('showSecurityIncidents') },
        { name: getLabel('threatAlerts'), link: '/threat-alerts', icon: Shield,        badge: activeAlerts },
        { name: getLabel('blacklist'),    link: '/blacklist',     icon: Ban },
      ].filter(i => i.show !== false),
    },
    {
      key: 'management', title: 'Management', icon: Briefcase,
      items: [
        { name: getLabel('members'),  link: '/members',  icon: Users },
        { name: getLabel('visitors'), link: '/visitors', icon: UserCheck },
        ...(sidebarConfig.showSpaces ? [{ name: getLabel('spaces'), link: '/spaces', icon: MapPin }] : []),
        { name: 'Devices', link: '/devices', icon: Fingerprint },
      ],
    },
    {
      key: 'integrations_root', title: null, icon: null,
      items: [
        { name: 'Integrations', link: '/integrations', icon: Zap },
      ],
    },
    {
      key: 'system', title: 'System', icon: Wrench,
      items: [
        { name: 'Profile',       link: '/settings?tab=profile',       icon: UserCheck  },
        { name: getLabel('settings'), link: '/settings?tab=organization', icon: Building2  },
        { name: 'Billing',       link: '/settings?tab=billing',       icon: BarChart2  },
        { name: 'Developer',     link: '/settings?tab=developer',     icon: Code2      },
      ],
    },
  ];

  // ── Event organizer — completely different nav ─────────────────────────────
  if (sidebarConfig.isEventOrganizer) {
    const eventItems = [
      { name: 'Overview',        link: '/events',           icon: LayoutDashboard },
      { name: 'Events',          link: '/events/list',      icon: CalendarDays },
      { name: 'Tickets & Orders',link: '/events/orders',    icon: ShoppingBag },
      { name: 'Wallet',          link: '/wallet',           icon: Wallet },
      { name: 'Integrations',    link: '/integrations',     icon: Zap },
      { name: 'Settings',        link: '/settings?tab=organization', icon: Settings },
    ];

    return (
      <>
        <style>{`
          .menu-row {
            display: flex; align-items: center; gap: 9px;
            padding: 7px 10px; border-radius: 7px;
            text-decoration: none; font-size: 13px; font-weight: 500;
            color: #52546A; letter-spacing: -0.1px;
            transition: background 0.12s, color 0.12s;
            cursor: pointer; user-select: none; width: 100%;
            border: none; background: transparent; text-align: left;
            font-family: inherit; -webkit-font-smoothing: antialiased;
            position: relative;
          }
          .menu-row:hover { background: #F5F5F8; color: #1C1C2E; }
          .menu-row:hover .menu-row-icon { color: #5341C4; }
          .menu-row.active { background: #F6F4FE; color: #3D2FA0; font-weight: 600; }
          .menu-row.active::before {
            content: ''; position: absolute; left: 0; top: 20%;
            height: 60%; width: 3px; background: #5341C4; border-radius: 0 3px 3px 0;
          }
          .menu-row.active .menu-row-icon { color: #5341C4; }
          .menu-row-icon {
            display: flex; align-items: center; justify-content: center;
            width: 18px; height: 18px; flex-shrink: 0;
            color: #9597A6; transition: color 0.12s;
          }
          .user-footer-section {
            position: absolute; bottom: 0; left: 0; right: 0;
            padding: 10px 12px; border-top: 1px solid #EEEEF4; background: #fff;
          }
          .plan-badge {
            display: flex; align-items: center; gap: 6px;
            padding: 6px 10px; border-radius: 7px;
            font-size: 11px; font-weight: 600; cursor: pointer;
            margin-bottom: 8px; transition: all 0.12s;
          }
          .plan-badge--trial     { background: #EFF6FF; color: #2563EB; border: 1px solid #DBEAFE; }
          .plan-badge--past-due  { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; }
          .plan-badge--suspended { background: #FFFBEB; color: #D97706; border: 1px solid #FEF3C7; }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ul
            className={`menu-nav ${layoutProps.ulClasses}`}
            style={{ flex: 1, margin: 0, padding: '8px 0 140px', listStyle: 'none' }}
          >
            {eventItems.map((item, i) => {
              const active = isActive(item.link);
              const Ic = item.icon;
              return (
                <li key={i} style={{ padding: '2px 10px', marginTop: i === 0 ? 6 : 0 }}>
                  <NavLink className={`menu-row ${active ? 'active' : ''}`} to={item.link}>
                    <span className="menu-row-icon">
                      <Ic size={ICON_SIZE} strokeWidth={ICON_STROKE} />
                    </span>
                    <span style={{ flex: 1 }}>{item.name}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="user-footer-section">
            {isTrial && (
              <div className="plan-badge plan-badge--trial" onClick={() => navigate('/settings?tab=billing&action=subscribe')}>
                <Zap size={11} strokeWidth={ICON_STROKE} /> Trial · {trialDaysLeft}d left — Upgrade
              </div>
            )}
            {isPastDue && (
              <div className="plan-badge plan-badge--past-due" onClick={() => navigate('/settings?tab=billing')}>
                <AlertCircle size={11} strokeWidth={ICON_STROKE} /> Payment Failed
              </div>
            )}
            {isSuspended && (
              <div className="plan-badge plan-badge--suspended" onClick={() => navigate('/settings?tab=billing')}>
                <XCircle size={11} strokeWidth={ICON_STROKE} /> Account Suspended
              </div>
            )}
            <OrgSwitcher />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        /* ── Shared row — all parents identical, clean neutral base ── */
        .menu-row {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 7px 10px;
          border-radius: 7px;
          text-decoration: none;
          font-size: 13px;
          font-weight: 500;
          color: #52546A;
          letter-spacing: -0.1px;
          transition: background 0.12s, color 0.12s;
          cursor: pointer;
          user-select: none;
          width: 100%;
          border: none;
          background: transparent;
          text-align: left;
          font-family: inherit;
          -webkit-font-smoothing: antialiased;
          position: relative;
        }
        .menu-row:hover {
          background: #F5F5F8;
          color: #1C1C2E;
        }
        .menu-row:hover .menu-row-icon { color: #5341C4; }

        /* Active — left accent line + very subtle tint, icon + text carry the colour */
        .menu-row.active {
          background: #F6F4FE;
          color: #3D2FA0;
          font-weight: 600;
        }
        .menu-row.active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 20%;
          height: 60%;
          width: 3px;
          background: #5341C4;
          border-radius: 0 3px 3px 0;
        }
        .menu-row.active .menu-row-icon { color: #5341C4; }

        /* ── Children — smaller, more muted at rest, indent ── */
        .menu-row-child {
          font-size: 12.5px;
          font-weight: 500;
          color: #7E8299;
          padding: 6px 10px 6px 13px;
        }
        .menu-row-child:hover { color: #2C2E3E; background: #F5F5F8; }
        .menu-row-child.active {
          color: #3D2FA0;
          font-weight: 600;
          background: #F6F4FE;
        }
        .menu-row-child.active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 20%;
          height: 60%;
          width: 2px;
          background: #8B7FE8;
          border-radius: 0 2px 2px 0;
        }

        /* ── Icon container ── */
        .menu-row-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          color: #9597A6;
          transition: color 0.12s;
        }

        /* ── Chevron ── */
        .menu-row-chevron {
          margin-left: auto;
          flex-shrink: 0;
          color: #CACCD8;
          transition: transform 0.2s ease;
        }
        .menu-row-chevron.open { transform: rotate(90deg); }

        /* ── Badge — light tinted, not solid purple ── */
        .menu-badge {
          padding: 1px 6px;
          background: #EDEAFC;
          color: #5341C4;
          font-size: 9.5px;
          font-weight: 700;
          border-radius: 8px;
          margin-left: auto;
          flex-shrink: 0;
          letter-spacing: 0.2px;
        }
        .menu-badge.num {
          background: #FDECEA;
          color: #C0392B;
        }
        .menu-badge.ai {
          background: linear-gradient(135deg, #EDEAFC 0%, #E4DCFF 100%);
          color: #5341C4;
        }

        /* ── Collapse wrapper ── */
        .menu-children {
          overflow: hidden;
          transition: max-height 0.22s ease, opacity 0.18s ease;
        }
        .menu-children.closed { max-height: 0 !important; opacity: 0; }
        .menu-children.open   { opacity: 1; }

        /* ── Bottom footer ── */
        .user-footer-section {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          padding: 10px 12px;
          border-top: 1px solid #EEEEF4;
          background: #fff;
        }
        .plan-badge {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 10px; border-radius: 7px;
          font-size: 11px; font-weight: 600; cursor: pointer;
          margin-bottom: 8px; transition: all 0.12s;
        }
        .plan-badge--trial     { background: #EFF6FF; color: #2563EB; border: 1px solid #DBEAFE; }
        .plan-badge--past-due  { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; }
        .plan-badge--suspended { background: #FFFBEB; color: #D97706; border: 1px solid #FEF3C7; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ul
          className={`menu-nav ${layoutProps.ulClasses}`}
          style={{ flex: 1, margin: 0, padding: '8px 0 140px', listStyle: 'none' }}
        >
          {sections.map((sec, si) => {
            const isOpen   = expanded[sec.key];
            const hasGroup = sec.title !== null;
            const SIcon    = sec.icon;

            // ── Grouped section (Security, Management, System) ───────────────
            if (hasGroup) {
              const anyChildActive = sec.items.some(item => isActive(item.link));
              return (
                <React.Fragment key={si}>
                  {/* Section header — looks identical to a nav item */}
                  <li style={{ padding: '2px 10px', margin: si > 0 ? '4px 0 0' : '4px 0 0' }}>
                    <button
                      className={`menu-row ${anyChildActive && !isOpen ? 'active' : ''}`}
                      onClick={() => toggle(sec.key)}
                    >
                      <span className="menu-row-icon">
                        <SIcon size={ICON_SIZE} strokeWidth={ICON_STROKE} />
                      </span>
                      <span style={{ flex: 1 }}>{sec.title}</span>
                      <span className={`menu-row-chevron ${isOpen ? 'open' : ''}`}>
                        <ChevronRight size={13} strokeWidth={ICON_STROKE} />
                      </span>
                    </button>
                  </li>

                  {/* Children */}
                  <div
                    className={`menu-children ${isOpen ? 'open' : 'closed'}`}
                    style={{ maxHeight: isOpen ? `${sec.items.length * 42}px` : '0px', paddingLeft: 8 }}
                  >
                    {sec.items.map((item, ii) => {
                      const active = isActive(item.link);
                      const Ic = item.icon;
                      return (
                        <li key={ii} style={{ padding: '1px 10px' }}>
                          <NavLink
                              className={() => `menu-row menu-row-child ${isActive(item.link) ? 'active' : ''}`}
                              to={item.link}
                            >
                            <span className="menu-row-icon">
                              <Ic size={ICON_SIZE} strokeWidth={ICON_STROKE} />
                            </span>
                            <span style={{ flex: 1 }}>{item.name}</span>
                            {item.badge > 0 && (
                              <span className="menu-badge num">{item.badge}</span>
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            }

            // ── Flat section (main, integrations) — direct nav items ─────────
            return (
              <React.Fragment key={si}>
                {sec.items.map((item, ii) => {
                  const active = isActive(item.link);
                  const Ic = item.icon;
                  return (
                    <li
                      key={ii}
                      style={{
                        padding: '2px 10px',
                        marginTop: si === 0 && ii === 0 ? 6 : 0,
                      }}
                    >
                      <NavLink
                        className={`menu-row ${active ? 'active' : ''}`}
                        to={item.link}
                      >
                        <span className="menu-row-icon">
                          <Ic size={ICON_SIZE} strokeWidth={ICON_STROKE} />
                        </span>
                        <span style={{ flex: 1 }}>{item.name}</span>
                        {item.badge && (
                          <span className={`menu-badge ${item.badge === 'AI' ? 'ai' : ''}`}>
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </React.Fragment>
            );
          })}
        </ul>

        {/* Footer */}
        <div className="user-footer-section">
          {isTrial && (
            <div className="plan-badge plan-badge--trial" onClick={() => navigate('/settings?tab=billing&action=subscribe')}>
              <Zap size={11} strokeWidth={ICON_STROKE} /> Trial · {trialDaysLeft}d left — Upgrade
            </div>
          )}
          {isPastDue && (
            <div className="plan-badge plan-badge--past-due" onClick={() => navigate('/settings?tab=billing')}>
              <AlertCircle size={11} strokeWidth={ICON_STROKE} /> Payment Failed
            </div>
          )}
          {isSuspended && (
            <div className="plan-badge plan-badge--suspended" onClick={() => navigate('/settings?tab=billing')}>
              <XCircle size={11} strokeWidth={ICON_STROKE} /> Account Suspended
            </div>
          )}
          <OrgSwitcher />
        </div>
      </div>
    </>
  );
}